import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import ComposeForm from "../components/compose/ComposeForm";
import { sendMail, saveDraft, updateDraft, deleteMail } from "../api/mailApi";
import { useVoiceToText } from "../hooks/useVoiceToText";
import {
  CORPORATE_SUFFIX,
  extractEmailAddress,
  isValidEmail,
  parseEmailList,
  validateEmailList,
  getLastEmailFragment,
  replaceLastEmailFragment,
} from "../utils/mailUtils";
import { SERVER_URL } from "../socket";
import useContacts from "../hooks/useContacts";

function ComposePage({
  compose,
  signatureList,
  defaultSignatureId,
  authHeaders,
  onMailSent,
  onNavigateSent,
  setToast,
  onDraftSaved,
  isMobile,
  ...props
}) {
  const [searchParams] = useSearchParams();
  const {
    editorRef,
    composeForm,
    setComposeForm,
    composeAttachments,
    setComposeAttachments,
    composeSignatureId,
    setComposeSignatureId,
    uploadingAttachment,
    isDragActive,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleAttachmentSelect,
    createComposeBody,
    createFinalBody,
    resetComposeForm,
    focusEditorStart,
    composeDraftId,
    signatureHtml,
    signatureEditable,
  } = compose;

  const { contacts } = useContacts();

  const didSendRef = useRef(false);
  const composeFormRef = useRef(composeForm);
  const composeAttachmentsRef = useRef(composeAttachments);
const [typingTo, setTypingTo] = useState("");
  const [typingField, setTypingField] = useState("to");

  useEffect(() => {
    composeFormRef.current = composeForm;
  }, [composeForm]);

  useEffect(() => {
    composeAttachmentsRef.current = composeAttachments;
  }, [composeAttachments]);

  const attachmentInputRef = useRef(null);
  const composeToInputRef = useRef(null);
  const composeCcInputRef = useRef(null);
  const composeBccInputRef = useRef(null);

  const [showCcField, setShowCcField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [invalidRecipientValue, setInvalidRecipientValue] = useState("");
  const [showInvalidRecipientModal, setShowInvalidRecipientModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const suggestionRequestRef = useRef(0);

  // Auto-show CC field if it has recipients (for Reply All)
  useEffect(() => {
    if (Array.isArray(composeForm.cc) && composeForm.cc.length > 0) {
      setShowCcField(true);
    }
  }, [composeForm.cc]);

  // Auto-show BCC field if it has recipients (for Reply All)
  useEffect(() => {
    if (Array.isArray(composeForm.bcc) && composeForm.bcc.length > 0) {
      setShowBccField(true);
      return;
    }
    if (typeof composeForm.bcc === 'string' && composeForm.bcc.trim().length > 0) {
      setShowBccField(true);
    }
  }, [composeForm.bcc]);
useEffect(() => {
  console.log("composeForm.bcc =", composeForm.bcc);
}, [composeForm.bcc]);
  // Voice-to-text functionality
  const {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
    resetTranscript
  } = useVoiceToText();

  const corporateRecipient = useMemo(() => {
    const emails = parseEmailList(addressToString(composeForm.to));
    const corporateEmails = emails.filter(email => 
      email.toLowerCase().endsWith(CORPORATE_SUFFIX)
    );
    return corporateEmails.length > 0 ? corporateEmails[0] : "";
  }, [composeForm.to]);
  
  function addressToString(value) {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "string") return value;
    return "";
  }
  useEffect(() => {
    if (composeForm.body) return;
    if (typeof createComposeBody !== "function") return;
    const defaultBody = createComposeBody("");
    if (!defaultBody) return;
    setComposeForm((prev) => ({ ...prev, body: defaultBody }));
    if (editorRef.current) {
      editorRef.current.innerHTML = defaultBody;
    }
  }, [composeForm.body, createComposeBody, setComposeForm]);

  useEffect(() => {
    setComposeSignatureId((prev) => (prev ?? defaultSignatureId));
  }, [defaultSignatureId, setComposeSignatureId]);

  // Handle URL parameters or prop for pre-filling fields
  useEffect(() => {
    let toParam = searchParams.get('to');
    // If composeTo prop is provided, use it (sanitize to string)
    if (props.composeTo && (!Array.isArray(composeForm.to) || composeForm.to.length === 0)) {
      if (typeof props.composeTo === 'string') {
        toParam = props.composeTo;
      } else if (typeof props.composeTo === 'object' && props.composeTo !== null) {
        // Try to extract email or name from object
        toParam = props.composeTo.email || props.composeTo.name || '';
      } else {
        toParam = '';
      }
    }
    if (toParam && (!Array.isArray(composeForm.to) || composeForm.to.length === 0)) {
      const parts = String(toParam || "").split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
      setComposeForm((prev) => ({ ...prev, to: parts }));
    }
  }, [searchParams, props.composeTo, composeForm.to, setComposeForm]);

  useEffect(() => {
    if (!authHeaders) {
      setToSuggestions([]);
      return;
    }

    const inputValue = typingTo.trim();
    if (!inputValue) {
      setToSuggestions([]);
      return;
    }

    const lastFragment = getLastEmailFragment(inputValue);
    if (!lastFragment) {
      setToSuggestions([]);
      return;
    }

    if (lastFragment.endsWith(CORPORATE_SUFFIX)) {
      setToSuggestions([]);
      return;
    }

    const prefix = lastFragment.includes("@") ? lastFragment.split("@")[0] : lastFragment;
    const requestId = ++suggestionRequestRef.current;
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      axios
        .get(`${SERVER_URL}/api/users/suggest`, {
          headers: authHeaders,
          params: { q: prefix },
          signal: controller.signal,
        })
        .then((res) => {
          if (suggestionRequestRef.current !== requestId) return;
          const users = Array.isArray(res.data?.users) ? res.data.users : [];
          const base = addressToString(composeForm[typingField]);
          const prefix = base ? (base.endsWith(",") ? base + " " : base + ", ") : "";

          const suggestions = users
            .map((u) => {
              const email = String(u?.userId || "").trim().toLowerCase();
              if (!email) return null;
              return {
                userId: prefix + email,
                fullName: u.fullName || "",
                displayEmail: email,
              };
            })
            .filter(Boolean)
            .slice(0, 8);
          // Add local group suggestions (from contacts) that match the last fragment
          const frag = lastFragment.toLowerCase();
          const groups = Array.from(new Set((contacts || [])
            .map(c => String(c.group || "").trim())
            .filter(Boolean)
            .map(g => g.toLowerCase())
          ));
          const typedFrag = String(lastFragment || "").toLowerCase().trim();
          const matchedGroups = groups
            .filter(g => g.startsWith(typedFrag) || g.includes(typedFrag))
            .map(g => ({ userId: prefix + g, fullName: "Group", displayEmail: g }));

          setToSuggestions([...matchedGroups, ...suggestions]);
        })
        .catch(() => {});
    }, 200);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [authHeaders, composeForm, typingTo, typingField]);

  // Expand group tokens (non-email entries) into member emails using local contacts
  const expandRecipientArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    const out = [];
    for (const token of arr) {
      const t = String(token || "").trim();
      if (!t) continue;
      if (isValidEmail(t)) {
        out.push(extractEmailAddress(t));
        continue;
      }
      const groupName = t.toLowerCase();
      const members = (contacts || [])
        .filter(c => String(c.group || "").trim().toLowerCase() === groupName)
        .flatMap(c => Array.isArray(c.emails) ? c.emails : (c.email ? [c.email] : []))
        .map(e => String(e || "").trim())
        .filter(Boolean);
      if (members.length) {
        out.push(...members);
      } else {
        // keep original token so validation will catch it as invalid
        out.push(t);
      }
    }
    return out;
  };

  // Handle voice-to-text transcript
  useEffect(() => {
    if (transcript && editorRef.current) {
      const currentContent = editorRef.current.innerHTML || '';
      const newContent = currentContent + (currentContent ? ' ' : '') + transcript;
      editorRef.current.innerHTML = newContent;
      setComposeForm(prev => ({ ...prev, body: newContent }));
      resetTranscript();
    }
  }, [transcript, editorRef, setComposeForm, resetTranscript]);

  const handleVoiceToText = useCallback(() => {
    if (!isSupported) {
      setToast?.("Voice input is not supported in this browser");
      return;
    }

    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isSupported, isListening, startListening, stopListening, setToast]);

  const openInvalidRecipientModal = useCallback((value) => {
    setInvalidRecipientValue(value);
    setShowInvalidRecipientModal(true);
  }, []);

  const closeInvalidRecipientModal = useCallback(() => {
    setShowInvalidRecipientModal(false);
    setInvalidRecipientValue("");
  }, []);

  const openAttachmentPicker = useCallback(() => {
    const input = attachmentInputRef.current;
    if (!input) return;
    input.value = "";
    input.click();
  }, []);

  const executeFormattingAction = useCallback((command, value = null) => {
    // try to restore last saved selection inside editor (so toolbar applies where caret was)
    let restored = false;
    if (typeof compose?.restoreSavedSelection === 'function') {
      try { restored = compose.restoreSavedSelection(); } catch (e) { restored = false; }
    }
    // fallback: focus caret at start
    if (!restored && typeof focusEditorStart === "function") {
      focusEditorStart();
    }
    try {
      // Some browsers require styleWithCSS for foreColor to apply via CSS instead of <font>
      if (command === 'foreColor') {
        try { document.execCommand('styleWithCSS', false, true); } catch {}
      }
      document.execCommand(command, false, value);
      if (command === 'foreColor') {
        try { document.execCommand('styleWithCSS', false, false); } catch {}
      }
    } catch {}
    if (editorRef.current) {
      setComposeForm((prev) => ({ ...prev, body: editorRef.current.innerHTML || "" }));
    }
  }, [focusEditorStart, editorRef, setComposeForm, compose]);

  const handleColorChange = useCallback((event) => {
    const value = event.target.value;
    if (!value) return;
    executeFormattingAction("foreColor", value);
  }, [executeFormattingAction]);


  const handleComposeAddressChange = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;
    console.log("ComposePage.handleComposeAddressChange", { name, value });
    setComposeForm((prev) => ({
      ...prev,
      [name]: Array.isArray(value) ? [...value] : [],
    }));
  }, [setComposeForm]);

  const handleComposeFieldChange = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;
    setComposeForm((prev) => ({ ...prev, [name]: value }));
  }, [setComposeForm]);

  const handleEditorInput = useCallback(() => {
    const node = editorRef.current;
    setComposeForm((prev) => ({ ...prev, body: node?.innerHTML || "" }));
  }, [editorRef, setComposeForm]);

  const handleRemoveAttachment = useCallback((index) => {
    setComposeAttachments((prev) => prev.filter((_, i) => i !== index));
  }, [setComposeAttachments]);

  const handleClearCompose = useCallback(() => {
    resetComposeForm();
    setShowCcField(false);
    setShowBccField(false);
    composeToInputRef.current?.focus();
  }, [resetComposeForm]);

  const handlePaste = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const file = new File([blob], `pasted-image.${type.split('/')[1]}`, { type });
            await handleAttachmentSelect({ target: { files: [file] } });
            break;
          } else if (type === 'text/plain') {
            const text = await item.getType(type);
            const textContent = await text.text();
            // restore previous caret position if available so paste goes where user had focus
            let restored = false;
            if (typeof compose?.restoreSavedSelection === 'function') {
              try { restored = compose.restoreSavedSelection(); } catch (e) { restored = false; }
            }
            if (!restored && typeof focusEditorStart === "function") {
              focusEditorStart();
            }
            document.execCommand('insertText', false, textContent);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Failed to paste from clipboard:', error);
    }
    if (editorRef.current) {
      setComposeForm((prev) => ({ ...prev, body: editorRef.current.innerHTML || "" }));
    }
  }, [focusEditorStart, editorRef, setComposeForm, handleAttachmentSelect]);

  const handleFilePaste = useCallback(async (files) => {
    const fileList = Array.from(files);
    if (!fileList.length) return;

    for (const file of fileList) {
      // Add as attachment
      await handleAttachmentSelect({ target: { files: [file] } });
    }
  }, [handleAttachmentSelect]);

  const attachmentToolbarHandlers = useMemo(() => ({
    onAction: executeFormattingAction,
    onColorChange: handleColorChange,
    onPaste: handlePaste,
  }), [executeFormattingAction, handleColorChange, handlePaste]);

  useEffect(() => {
    composeToInputRef.current?.focus();
  }, []);

  const handleComposeSubmit = useCallback(async (event) => {
    event.preventDefault();

    const mailStatus = composeForm.mailStatus || "NA";
    if (mailStatus === "NA") {
      setToast?.("Please select mail status.");
      return;
    }

    // If 'Save only (do not send)' selected, skip recipient/subject validations and save as draft
    if (mailStatus === "NoSend") {
      if (!authHeaders) {
        setToast?.("Authentication required to save.");
        return;
      }
      setIsSending(true);
      try {
        const res = await handleSaveDraft();
        setToast?.("Saved (not sent).");
        resetComposeForm();
        setShowCcField(false);
        setShowBccField(false);
        return;
      } catch (err) {
        const message = err?.response?.data?.error || "Failed to save.";
        setToast?.(message);
        return;
      } finally {
        setIsSending(false);
      }
    }

    // Expand any group tokens into member emails before validation
    const toExpanded = expandRecipientArray(composeForm.to || []);
    const ccExpanded = expandRecipientArray(composeForm.cc || []);
    const bccExpanded = expandRecipientArray(composeForm.bcc || []);

    const toAddressRaw = Array.isArray(toExpanded) ? toExpanded.join(", ") : "";
    const ccAddressRaw = Array.isArray(ccExpanded) ? ccExpanded.join(", ") : "";
    const bccAddressRaw = Array.isArray(bccExpanded) ? bccExpanded.join(", ") : "";
    const subject = composeForm.subject.trim();

    // Validate To field
    const toValidation = validateEmailList(toAddressRaw);
    if (!toValidation.isValid) {
      if (toValidation.emails.length === 0) {
        setToast?.("At least one recipient email is required.");
        return;
      }
      if (toValidation.invalidEmails.length > 0) {
        openInvalidRecipientModal(toValidation.invalidEmails.join(", "));
        return;
      }
    }

    // Validate Cc field if present
    let ccEmails = [];
    if (ccAddressRaw) {
      const ccValidation = validateEmailList(ccAddressRaw);
      if (!ccValidation.isValid && ccValidation.invalidEmails.length > 0) {
        openInvalidRecipientModal(ccValidation.invalidEmails.join(", "));
        return;
      }
      ccEmails = ccValidation.emails;
    }

    // Validate Bcc field if present
    let bccEmails = [];
    if (bccAddressRaw) {
      const bccValidation = validateEmailList(bccAddressRaw);
      if (!bccValidation.isValid && bccValidation.invalidEmails.length > 0) {
        openInvalidRecipientModal(bccValidation.invalidEmails.join(", "));
        return;
      }
      bccEmails = bccValidation.emails;
    }

    if (!subject) {
      setToast?.("Subject is required.");
      return;
    }

    // Create recipient list and check for duplicates
    const allRecipients = [...toValidation.emails, ...ccEmails, ...bccEmails];
    const recipientSet = new Set(allRecipients.map(email => email.toLowerCase()));
    
    if (recipientSet.size !== allRecipients.length) {
      setToast?.("Duplicate email addresses found. Please remove duplicates.");
      return;
    }

    if (!authHeaders) {
      setToast?.("Authentication required to send mail.");
      return;
    }

    setIsSending(true);
    try {
      const data = await sendMail(
        {
          to: toValidation.emails.join(", "),
          cc: ccEmails.length > 0 ? ccEmails.join(", ") : undefined,
          bcc: bccEmails.length > 0 ? bccEmails.join(", ") : undefined,
          subject,
          body: createFinalBody(composeForm.body),
          attachments: composeAttachments,
          mailStatus: composeForm.mailStatus,
          replyToId: composeForm.replyToId || undefined,
        },
        authHeaders
      );

      const newMail = data?.mail;
      if (typeof onMailSent === "function") {
        onMailSent(newMail || null);
      }
      if (typeof onNavigateSent === "function" && newMail) {
        onNavigateSent(newMail);
      }

      // If composing from a draft, delete the draft now that mail was sent
      try {
        if (composeDraftId) {
          await deleteMail(composeDraftId, authHeaders);
        }
      } catch (err) {
        // ignore delete errors
      }

      resetComposeForm();
      setShowCcField(false);
      setShowBccField(false);

      const delivered = Boolean(data?.delivered);
      setToast?.(delivered ? "Mail delivered to recipient inbox." : "Mail sent successfully.");
      didSendRef.current = true;
    } catch (err) {
      const message = err.response?.data?.error || "Failed to send mail.";
      setToast?.(message);
    } finally {
      setIsSending(false);
    }
  }, [
    authHeaders,
    composeAttachments,
    composeForm.body,
    composeForm.bcc,
    composeForm.cc,
    composeForm.subject,
    composeForm.to,
    onMailSent,
    onNavigateSent,
    openInvalidRecipientModal,
    resetComposeForm,
    setToast,
    contacts,  // Added to dependencies
    CORPORATE_SUFFIX,  // Added to dependencies
  ]);

  const handleSaveDraft = useCallback(async () => {
    if (!authHeaders) {
      setToast?.("Authentication required to save draft.");
      return;
    }
// ✅ AFTER — addressToString converts [] → "" safely
const current = composeFormRef.current || {};
const toValue = addressToString(current.to).trim();

// Don't save draft at all if `to` is empty (prevents null violation)
if (!toValue && !current.subject?.trim() && !current.body?.trim()) {
  return;
}

const payload = {
  to: toValue || "",                        // ✅ always a string
  cc: addressToString(current.cc) || "",   // ✅ always a string
  bcc: addressToString(current.bcc) || "", // ✅ always a string
  subject: current.subject || "",
  body: current.body || "",
  attachments: composeAttachmentsRef.current || [],
  folder: "draft",
  mailStatus: current.mailStatus || "NA",
  replyToId: current.replyToId || undefined,
};
    try {
      let res = null;
      if (composeDraftId) {
        res = await updateDraft(composeDraftId, payload, authHeaders);
      } else {
        res = await saveDraft(payload, authHeaders);
      }
      setToast?.("Draft saved.");
      try {
        onDraftSaved?.(res?.mail || null);
      } catch {}
      return res;
    } catch (err) {
      console.error('Save draft failed (manual):', err);
      const message = err?.response?.data?.error || err?.message || "Failed to save draft.";
      setToast?.(message);
    }
  }, [authHeaders, setToast]);

  // Debounced autosave when compose fields or attachments change
  const autosaveTimeoutRef = useRef(null);
  const lastSavedDraftRef = useRef(null);

  useEffect(() => {
    if (didSendRef.current) return;
    if (!authHeaders) return;

    const current = composeFormRef.current || {};
    const attachmentsNow = composeAttachmentsRef.current || [];
    // Autosave when there is any meaningful compose data
    // Save if body has text OR subject/recipients/attachments are present
    const bodyText = current.body ? String(current.body).trim() : "";
    const toText = addressToString(current.to);
   
const hasContent =
  (toText && toText.trim()) ||
  bodyText.length > 0 ||
  (current.subject && String(current.subject).trim());

    if (!hasContent) return;

    // Debounce
  autosaveTimeoutRef.current = setTimeout(async () => {
      try {
        // ✅ READ LATEST STATE (current is stale from closure — use refs)
        const snap = composeFormRef.current || {};
        const attachmentsSnap = composeAttachmentsRef.current || [];

        // ✅ GUARD — never autosave if `to` is empty
        const toValue = addressToString(snap.to).trim();
        if (!toValue) return;

        const payload = {
          to:      toValue,
          cc:      addressToString(snap.cc).trim()  || "",
          bcc:     addressToString(snap.bcc).trim() || "",
          subject: snap.subject  || "",
          body:    snap.body     || "",
          attachments: attachmentsSnap,
          folder: "draft",
          mailStatus: snap.mailStatus || "NA",
        };

        // Avoid repeated identical saves
        const fingerprint = JSON.stringify(payload);
        if (lastSavedDraftRef.current === fingerprint) return;

        let res = null;
        if (composeDraftId) {
          res = await updateDraft(composeDraftId, payload, authHeaders);
        } else {
          res = await saveDraft(payload, authHeaders);
        }
        lastSavedDraftRef.current = fingerprint;
        try {
          onDraftSaved?.(res?.mail || null);
        } catch {}

      } catch (err) {
        console.error("Autosave draft failed:", err);
        // ✅ Silence null-violation errors so user doesn't see them
        const msg = err?.response?.data?.error || err?.message || "";
        if (!msg.toLowerCase().includes("cannot be null")) {
          setToast?.(msg || "Failed to autosave draft.");
        }
      }
    }, 1000);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [authHeaders, setToast, composeFormRef, composeAttachmentsRef]);


  return (
    <section className="mail-compose">
      <ComposeForm
        composeForm={composeForm}
        onAddressChange={handleComposeAddressChange}
        onTypingToChange={(field, val) => { setTypingTo(val); setTypingField(field); }}
        onFieldChange={handleComposeFieldChange}
        composeAttachments={composeAttachments}
        onRemoveAttachment={handleRemoveAttachment}
        onSubmit={handleComposeSubmit}
        onClear={handleClearCompose}
        signatureList={signatureList}
        composeSignatureId={composeSignatureId}
        onSignatureChange={(event) => setComposeSignatureId(event.target.value)}
        defaultSignatureId={defaultSignatureId}
        editorRef={editorRef}
        onEditorInput={handleEditorInput}
        isDragActive={isDragActive}
        handleDragEnter={handleDragEnter}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        uploadingAttachment={uploadingAttachment}
        attachmentInputRef={attachmentInputRef}
        onAttachmentSelect={handleAttachmentSelect}
        onOpenAttachmentPicker={openAttachmentPicker}
        onSaveDraft={handleSaveDraft}
        toSuggestions={toSuggestions}
        contacts={contacts}
        corporateRecipient={corporateRecipient}
        showCcField={showCcField}
        onTypingTo={setTypingTo}
        onToggleCc={(next) => {
          if (!next) {
            setComposeForm((prev) => ({ ...prev, cc: [] }));
          }
          setShowCcField(next);
          if (next) {
            setTimeout(() => composeCcInputRef.current?.focus(), 0);
          }
        }}
        showBccField={showBccField}
        onToggleBcc={(next) => {
          if (!next) {
            setComposeForm((prev) => ({ ...prev, bcc: [] }));
          }
          setShowBccField(next);
          if (next) {
            setTimeout(() => composeBccInputRef.current?.focus(), 0);
          }
        }}
        composeCcInputRef={composeCcInputRef}
        composeBccInputRef={composeBccInputRef}
        composeToInputRef={composeToInputRef}
        onFocusToField={() => {}}
        toolbarHandlers={attachmentToolbarHandlers}
        isSending={isSending}
        onVoiceToText={handleVoiceToText}
        isListening={isListening}
        onFilePaste={handleFilePaste}
        isMobile={isMobile}
        signatureHtml={signatureHtml}
        signatureEditable={signatureEditable}
      />

      {showInvalidRecipientModal && (
        <div
          className="mail-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeInvalidRecipientModal();
            }
          }}
        >
          <div className="mail-modal">
            <h2>Invalid recipient</h2>
            <p className="mail-modal-subtitle">Please enter a valid email address.</p>
            {invalidRecipientValue && <div className="mail-modal-error">{invalidRecipientValue}</div>}
            <div className="mail-modal-actions">
              <button type="button" onClick={closeInvalidRecipientModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ComposePage;
